import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({
    providedIn: 'root'
})
export class Seo {
    private canonicalTag: HTMLElement;
    private meta: Meta;
    private title: Title;

    private defaults: object = {
        title: 'Xfinity Mobile',
        robots: 'index, follow',
        og: {
            type: 'website',
            site_name: 'XFINITY',
            image: 'https://www.xfinity.com/mobile-static/xfinitymobile-logo.jpg'
        },
        twitter: {
            card: 'summary',
            site: '@xfinitymobile',
            image: 'https://www.xfinity.com/mobile-static/xfinitymobile-logo.jpg'
        }
    };

    constructor(meta: Meta, title: Title) {
        Object.assign(this, { meta, title });
    }

    public addPrerender404Tag(): void {
        this.meta.addTag({ name: 'prerender-status-code', content: '404' });
    }

    public addPrerender423Tag(): void {
        this.meta.addTag({ name: 'prerender-status-code', content: '423' });
    }

    public addPrerender301Tag(newLocation: string): void {
        this.meta.addTag({ name: 'prerender-header', content: `Location: ${newLocation}` });
        this.meta.addTag({ name: 'prerender-status-code', content: '301' });
    }

    public updateMetaTags(metaData: MetaDataDefault = {}): void {
        const url: string = location.href.substr(0, location.href.length);
        const computedTags: object = {
            url,
            canonical: url,
            og: {
                url
            },
            twitter: {
                url
            }
        };

        const merge: object = this.mergeTags(this.defaults, computedTags, metaData);
        this.processMetaTags(merge);
    }

    private mergeTags(...args: object[]): object {
        const merged: object = {};

        args.forEach((mergable: object) => {
            Object.keys(mergable).forEach((key: string) => {
                const type: string = typeof mergable[key];
                if (type === 'string') {
                    merged[key] = mergable[key];
                } else if (type === 'object') {
                    merged[key] = { ...merged[key], ...mergable[key] };
                }
            });
        });

        return merged;
    }

    private processMetaTags(data: object, previousKey: string = ''): void {
        const metaData: object = data[previousKey] || data; // Try to access the inner object before the outer object

        Object.keys(metaData).forEach((key: string) => {
            const type: string = typeof metaData[key];
            if (type === 'string') {
                const tagName: string = previousKey.length ? `${previousKey}:${key}` : key;
                if (previousKey === '' && key === 'title') {
                    // create an element to get HTML Entities to load correctly
                    let ele: HTMLElement = window.document.createElement('span');
                    ele.innerHTML = metaData[key];
                    this.title.setTitle(ele.innerText);
                    if (ele.remove) {
                        ele.remove();
                    } else {
                        ele = undefined;
                    }
                } else if (previousKey === 'og') {
                    this.meta.updateTag({ property: tagName, content: metaData[key] });
                } else if (key === 'canonical') {
                    if (!this.canonicalTag) {
                        this.canonicalTag = window.document.createElement('link');
                        this.canonicalTag.setAttribute('rel', 'canonical');

                        window.document.head.appendChild(this.canonicalTag);
                    }

                    this.canonicalTag.setAttribute('href', metaData[key]);
                } else {
                    this.meta.updateTag({ name: tagName, content: metaData[key] });
                }
            } else if (type === 'object') {
                this.processMetaTags(metaData[key], key);
            }
        });
    }
}
